import React, { useState, useEffect } from 'react';
import { usersAPI, boutiquesAPI } from '../../services/api';
import { Plus, Edit, Trash2, X, Users as UsersIcon } from 'lucide-react';
import { API_URL } from '../../services/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [ouvriersAtelier, setOuvriersAtelier] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nom_complet: '',
    email: '',
    role: 'boutique',
    boutique_id: '',
    ouvrier_id: '',
    actif: true
  });

  useEffect(() => {
    loadUsers();
    loadBoutiques();
    loadOuvriers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadBoutiques = async () => {
    try {
      const response = await boutiquesAPI.getAll();
      setBoutiques(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadOuvriers = async () => {
    try {
      const r = await fetch(`${API_URL}/ouvriers`);
      const d = await r.json();
      setOuvriersAtelier(Array.isArray(d) ? d : []);
    } catch (e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (editingUser && !data.password) {
        delete data.password;
      }
      
      if (editingUser) {
        await usersAPI.update(editingUser.id, data);
      } else {
        await usersAPI.create(data);
      }
      loadUsers();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Désactiver cet utilisateur ?')) {
      try {
        await usersAPI.delete(id);
        loadUsers();
      } catch (error) {
        alert(error.response?.data?.error || 'Erreur');
      }
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        nom_complet: user.nom_complet,
        email: user.email || '',
        role: user.role,
        boutique_id: user.boutique_id || '',
        ouvrier_id: user.ouvrier_id || '',
        actif: user.actif
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        nom_complet: '',
        email: '',
        role: 'boutique',
        boutique_id: '',
        ouvrier_id: '',
        actif: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const ROLE_CONFIG = {
    admin:        { label: 'Admin',        color: 'bg-red-100 text-red-800' },
    boutique:     { label: 'Boutique',     color: 'bg-blue-100 text-blue-800' },
    atelier:      { label: 'Atelier',      color: 'bg-purple-100 text-purple-800' },
    chef_atelier: { label: 'Chef Atelier', color: 'bg-yellow-100 text-yellow-800' },
    ouvrier:      { label: 'Ouvrier',      color: 'bg-green-100 text-green-800' },
  };

  const getRoleBadge = (role) => {
    const cfg = ROLE_CONFIG[role] || { label: role, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-2">
          <UsersIcon className="h-8 w-8" />
          <span>Gestion des utilisateurs</span>
        </h2>
        <button
          onClick={() => openModal()}
          className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Ajouter un utilisateur</span>
        </button>
      </div>

      {/* Table desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom complet</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Boutique / Ouvrier lié</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.username}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.nom_complet}</td>
                <td className="px-6 py-4 text-sm">{getRoleBadge(user.role)}</td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {user.boutique_nom || (user.ouvrier_id ? `Ouvrier #${user.ouvrier_id}` : '-')}
                </td>
                <td className="px-6 py-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    user.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.actif ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-medium">
                  <button
                    onClick={() => openModal(user)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="md:hidden space-y-3">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold text-gray-900">{user.username}</div>
                <div className="text-xs text-gray-500">{user.nom_complet}</div>
              </div>
              {getRoleBadge(user.role)}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span>{user.boutique_nom || '-'}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                user.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {user.actif ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openModal(user)}
                className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium"
              >
                Modifier
              </button>
              <button
                onClick={() => handleDelete(user.id)}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 sm:p-8 max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe {editingUser && <span className="font-normal text-gray-400">(optionnel)</span>}
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                  <input
                    type="text"
                    required
                    value={formData.nom_complet}
                    onChange={(e) => setFormData({ ...formData, nom_complet: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="admin">Admin — accès total</option>
                  <option value="chef_atelier">Chef Atelier — gère la production</option>
                  <option value="atelier">Atelier — accès atelier complet</option>
                  <option value="ouvrier">Ouvrier — ses tâches uniquement</option>
                  <option value="boutique">Boutique — vente & stock</option>
                </select>
              </div>

              {formData.role === 'boutique' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Boutique assignée</label>
                  <select
                    value={formData.boutique_id}
                    onChange={(e) => setFormData({ ...formData, boutique_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Sélectionner une boutique</option>
                    {boutiques.map((boutique) => (
                      <option key={boutique.id} value={boutique.id}>
                        {boutique.nom} ({boutique.type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.role === 'ouvrier' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profil ouvrier lié <span className="text-xs text-gray-400">(pour voir ses tâches)</span>
                  </label>
                  <select
                    value={formData.ouvrier_id}
                    onChange={(e) => setFormData({ ...formData, ouvrier_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">-- Sélectionner un ouvrier --</option>
                    {ouvriersAtelier.map((ov) => (
                      <option key={ov.id} value={ov.id}>
                        {ov.nom} {ov.prenom} — {ov.specialite} ({ov.matricule})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editingUser && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.actif}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Utilisateur actif</label>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
